/**
 * Мониторинг качества сети
 * Определяет тип соединения, скорость, задержку
 */

export interface NetworkQuality {
  type: string  // '4g', '3g', 'wifi', 'ethernet', 'unknown'
  effectiveType: string  // 'slow-2g', '2g', '3g', '4g'
  downlink: number  // Mbps
  rtt: number  // ms (round-trip time)
  bars: number  // 1-5 палочек
  operator: string | null  // Оператор связи (не доступно через Web API)
}

/**
 * Сервис мониторинга качества сети
 */
export class NetworkMonitor {
  /**
   * Получает текущее качество сети
   */
  static getQuality(): NetworkQuality {
    // @ts-ignore - NetworkInformation может быть не в типах
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection
    
    if (!connection) {
      // Fallback если API не поддерживается
      return {
        type: 'unknown',
        effectiveType: 'unknown',
        downlink: 0,
        rtt: 0,
        bars: 3,
        operator: null
      }
    }
    
    // Определяем количество палочек по effectiveType и rtt
    let bars = 5
    const effectiveType = connection.effectiveType || 'unknown'
    const rtt = connection.rtt || 0
    
    if (effectiveType === 'slow-2g') {
      bars = 1
    } else if (effectiveType === '2g') {
      bars = 2
    } else if (effectiveType === '3g') {
      bars = 3
    } else if (effectiveType === '4g') {
      // Для 4G смотрим на RTT
      if (rtt > 300) bars = 2
      else if (rtt > 200) bars = 3
      else if (rtt > 100) bars = 4
      else bars = 5
    }
    
    return {
      type: connection.type || 'unknown',
      effectiveType: effectiveType,
      downlink: connection.downlink || 0,
      rtt: rtt,
      bars: bars,
      operator: null  // Не доступно через Web API
    }
  }
  
  /**
   * Измеряет задержку до указанного URL
   */
  static async measureLatency(url: string): Promise<number> {
    const start = performance.now()
    try {
      await fetch(url, { 
        method: 'HEAD', 
        cache: 'no-cache',
        mode: 'no-cors'  // Чтобы избежать CORS ошибок
      })
      return Math.round(performance.now() - start)
    } catch (err) {
      console.warn('[NetworkMonitor] Failed to measure latency:', err)
      return -1
    }
  }
  
  /**
   * Определяет, онлайн ли устройство
   */
  static isOnline(): boolean {
    return navigator.onLine
  }
  
  /**
   * Подписывается на изменения состояния сети
   */
  static onNetworkChange(callback: (quality: NetworkQuality) => void): () => void {
    const handleChange = () => {
      callback(NetworkMonitor.getQuality())
    }
    
    window.addEventListener('online', handleChange)
    window.addEventListener('offline', handleChange)
    
    // @ts-ignore
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection
    
    if (connection) {
      connection.addEventListener('change', handleChange)
    }
    
    // Возвращаем функцию для отписки
    return () => {
      window.removeEventListener('online', handleChange)
      window.removeEventListener('offline', handleChange)
      if (connection) {
        connection.removeEventListener('change', handleChange)
      }
    }
  }
  
  /**
   * Форматирует скорость в читаемый вид
   */
  static formatSpeed(mbps: number): string {
    if (mbps === 0) return 'Unknown'
    if (mbps < 1) return `${Math.round(mbps * 1000)} Kbps`
    return `${mbps.toFixed(1)} Mbps`
  }
  
  /**
   * Возвращает эмодзи для количества палочек
   */
  static getBarsEmoji(bars: number): string {
    const emojis = ['📵', '📶', '📶', '📶', '📶', '📶']
    return emojis[bars] || '📶'
  }
}
